import React from 'react';
import { Home, Building, Shield } from 'lucide-react';

const AUDIENCES = [
  {
    icon: Home,
    title: 'For Residents',
    img: '/landing/audience-resident.jpg',
    bullets: [
      'Pre-approve guests with a tap, share QR or 6-digit code',
      'Live vehicle entry alerts when family arrives home',
      'Notices, surveys, neighbour chat — all in one place',
    ],
    accent: '#14D4C6',
  },
  {
    icon: Building,
    title: 'For Management',
    img: '/landing/audience-management.jpg',
    bullets: [
      'Live traffic analytics, vehicle approvals, staff oversight',
      'Surveys & polls with explicit expiry, post-vote stats',
      'Monthly digest, weekly pulse — automated reports',
    ],
    accent: '#60A5FA',
  },
  {
    icon: Shield,
    title: 'For Guards',
    img: '/landing/audience-guard.jpg',
    bullets: [
      'Live ANPR feed, manual plate verification, QR scan',
      'Pending visitors poll, delivery pre-approvals lookup',
      'Visitor registration without phone or paperwork',
    ],
    accent: '#F59E0B',
  },
];

export const AudienceCards = () => (
  <section
    id="audience"
    className="py-24 lg:py-32 bg-slate-50 text-slate-900 relative"
    data-testid="audience-section"
  >
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <div className="max-w-2xl">
        <p
          className="text-base md:text-lg tracking-wide uppercase text-[#0E9F94] font-semibold mb-3"
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          Built for everyone at the gate
        </p>
        <h3
          className="text-3xl sm:text-4xl lg:text-5xl tracking-tight font-bold leading-tight text-slate-900"
          style={{ fontFamily: 'Outfit, sans-serif' }}
          data-testid="audience-headline"
        >
          Three roles, one calmly orchestrated app.
        </h3>
      </div>

      <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
        {AUDIENCES.map((a, i) => {
          const Icon = a.icon;
          return (
            <div
              key={a.title}
              className="group relative rounded-2xl overflow-hidden bg-[#0B1120] border border-slate-800 shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
              data-testid={`audience-card-${i}`}
            >
              <div className="aspect-[4/3] overflow-hidden bg-slate-800">
                <img
                  src={a.img}
                  alt={a.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </div>
              <div className="p-6">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center mb-4"
                  style={{ backgroundColor: `${a.accent}20` }}
                >
                  <Icon className="w-5 h-5" style={{ color: a.accent }} />
                </div>
                <h4
                  className="text-xl font-semibold tracking-tight mb-3 text-white"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  {a.title}
                </h4>
                <ul
                  className="space-y-2.5"
                  style={{ fontFamily: 'Work Sans, sans-serif' }}
                >
                  {a.bullets.map((b, j) => (
                    <li
                      key={j}
                      className="text-sm text-slate-300 leading-relaxed flex items-start gap-2"
                      data-testid={`audience-${i}-bullet-${j}`}
                    >
                      <span
                        className="mt-1.5 w-1 h-1 rounded-full shrink-0"
                        style={{ backgroundColor: a.accent }}
                      />
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);
